import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import Tone from 'tone';
import { Button, Col, Row } from 'antd';
import withHarpInstrument from '../hocs/withHarpIntrument';
import Sadko from '../../service/Sadko';
import withTune from '../hocs/withTune';

const ControlPanel = styled.div`
    text-align: center;
    overflow: hidden;
`;

function TabControl({ song, onChangeSongPosition, instrument = {}, tune }) {
    const [playing, setPlaying] = useState(false);
    const [tabActive, setTabActive] = useState(false);

    const songPosition = useRef(-1);
    const songPart = useRef();

    const stepPart = useRef();

    const notes = song.tracks[0].notes.map((note) => ({ ...note, name: Sadko.transposeNoteByTune(note.name, tune) }));
    const lastNote = notes[notes.length - 1];

    useEffect(() => {
        const listener = (event) => {
            if (!tabActive) {
                return true;
            }

            switch (event.code) {
                case 'ArrowRight':
                    nextStep();
                    break;
                case 'ArrowLeft':
                    prevStep();
                    break;
                case 'Space':
                    playing ? pause() : play();
                    break;

                default:
            }

            return true;
        };

        document.addEventListener('keydown', listener, false);

        return () => {
            document.removeEventListener('keydown', listener)
        };
    });

    /**
     *
     */
    const timeBlocks = useMemo(() => {
        const result = {};

        notes.forEach((note) => {
            if (!(note.time in result)) {
                result[note.time] = [];
            }

            result[note.time].push(note);
        });
        return result;
    }, [notes]);

    /**
     *
     * @type {number[]}
     */
    const timeSteps = useMemo(() => {
        return Object.keys(timeBlocks).map(parseFloat).sort((a, b) => a - b);
    }, [timeBlocks]);

    useEffect(() => {
        const initPlayer = () => {
            if (!instrument.ready) {
                return;
            }

            const playNote = (time, note) => {
                instrument.playNote(time, note);

                if (songPosition.current !== note.time) {
                    songPosition.current = note.time;
                    setTimeout(() => onChangeSongPosition(songPosition.current), 0);
                }

                if (note === lastNote) {
                    setTimeout(() => {
                        setPlaying(false);
                        songPosition.current = -1;
                        pause();
                        onChangeSongPosition(songPosition.current)
                    }, 1000);
                }
            };

            const trackData = {
                bpm: song.header.tempos[0].bpm,
                timeSignature: song.header.timeSignatures[0].timeSignature
            };

            Tone.Transport.bpm.value = trackData.bpm;
            Tone.Transport.timeSignature = trackData.timeSignature;

            songPart.current = new Tone.Part(playNote, notes);
            songPart.current.humanize = song.needHumanize();
        };

        initPlayer();
    }, [song, notes, instrument, lastNote, onChangeSongPosition]);

    const play = () => {
        setTabActive(true);

        if (stepPart.current) {
            stepPart.current.cancel()
        }

        Tone.Transport.stop();
        songPart.current.cancel();
        Tone.Transport.cancel();

        songPart.current.start(0, songPosition.current);
        Tone.Transport.start();

        setPlaying(true);
    };

    const pause = () => {
        Tone.Transport.stop();
        Tone.Transport.cancel();

        songPart.current.stop();
        songPart.current.cancel();

        setPlaying(false);
    };

    const nextStep = () => {
        setTabActive(true);
        move();
    };

    const prevStep = () => {
        setTabActive(true);
        move(-1);
    };

    const move = (direction = 1) => {
        if (playing) {
            return;
        }
        const currentStepIndex = timeSteps.indexOf(songPosition.current);

        setPlaying(false);

        songPart.current.cancel();
        Tone.Transport.stop();
        Tone.Transport.cancel();

        if (stepPart.current) {
            stepPart.current.cancel()
        }

        let nextIndex = (currentStepIndex + direction) % timeSteps.length;
        if (nextIndex < 0) {
            nextIndex = timeSteps.length - 1;
        }

        stepPart.current = new Tone.Part(instrument.playNote, timeBlocks[timeSteps[nextIndex]]);
        songPosition.current = timeSteps[nextIndex];

        stepPart.current.start(0, songPosition.current);
        Tone.Transport.start();

        onChangeSongPosition(songPosition.current);
    };

    const togglePlayer = (event) => {
        if (!event.clientX) {
            return;
        }
        playing ? pause() : play(songPosition.current);
    };

    const initializing = !song || !instrument.ready;

    return <ControlPanel>
        <Row gutter={{ xs: 48, sm: 16, md: 24, lg: 32 }} type='flex' justify='center'>
            <Col span={2}>
                <Button onClick={prevStep} shape='circle-outline' icon='step-backward'
                        disabled={initializing || playing}/>
            </Col>
            <Col span={2}>
                <Button onClick={togglePlayer} shape='circle-outline' icon={playing ? 'pause' : 'play-circle'}
                        disabled={initializing}/>
            </Col>
            <Col span={2}>
                <Button onClick={nextStep} shape='circle-outline' icon='step-forward'
                        disabled={initializing || playing}/>
            </Col>
        </Row>
    </ControlPanel>
}

export default React.memo(withHarpInstrument(withTune(TabControl)));
